import type { FulfillmentStatusKey } from '@/types/quote';
import type { ApprovalRejection, QuoteStatus, QuoteSummary } from '@/hooks/useQuotes';

interface QuoteStatusMeta {
  label: string;
  toneKey: string;
}

export const QUOTE_STATUS_META: Record<string, QuoteStatusMeta> = {
  Draft: { label: 'Draft', toneKey: 'draft' },
  PendingApproval: { label: 'Pending Approval', toneKey: 'pending' },
  Approved: { label: 'Approved', toneKey: 'approved' },
  Submitted: { label: 'Submitted', toneKey: 'sent' },
  Sent: { label: 'Sent', toneKey: 'sent' },
  Accepted: { label: 'Accepted', toneKey: 'won' },
  Declined: { label: 'Declined', toneKey: 'lost' },
  Expired: { label: 'Expired', toneKey: 'expired' },
  Revoked: { label: 'Revoked', toneKey: 'revoked' },
  Converted: { label: 'Converted', toneKey: 'won' },
};

export const CHANGES_REQUESTED_LABEL = 'Changes requested';

export function quoteStatusBadge(
  status: string,
  changesRequested?: ApprovalRejection | null,
): { value: string; label: string } {
  if (status === 'Draft' && changesRequested) return { value: 'changesrequested', label: CHANGES_REQUESTED_LABEL };
  const meta = Object.prototype.hasOwnProperty.call(QUOTE_STATUS_META, status)
    ? QUOTE_STATUS_META[status]
    : undefined;
  if (meta) return { value: meta.toneKey, label: meta.label };
  return { value: 'draft', label: status.trim() || 'Unknown' };
}

export function canCreateQuoteVersion(status: QuoteStatus): boolean {
  return status === 'Revoked' || status === 'Declined' || status === 'Expired';
}

export const TYPE_LABELS: Record<string, string> = {
  NewBusiness: 'New',
  Renewal: 'Renewal',
  RenewalWithChanges: 'Changes',
  Amendment: 'Amend',
  ProofOfConcept: 'Proof of Concept',
};

export const CANCELLATION_TYPE_LABEL = 'Cancellation';

// A quote that only ends subscriptions is saved as an Amendment (the HubSpot Churn deal type maps
// there), but reads as what it does.
export function quoteTypeLabel(quote: Pick<QuoteSummary, 'quoteType' | 'cancelOnly'>): string {
  if (quote.cancelOnly) return CANCELLATION_TYPE_LABEL;
  return TYPE_LABELS[quote.quoteType] ?? quote.quoteType;
}

// `fulfillmentStatus` filters on a different axis than `statuses`: a manual quote awaiting hand
// provisioning is already Accepted or Converted, so a status list alone cannot single it out.
export const QUOTE_WORKFLOW_TABS = [
  { value: 'all', label: 'All', statuses: [] },
  { value: 'needs-action', label: 'Needs action', statuses: ['Draft', 'Approved', 'Submitted'] },
  { value: 'awaiting-approval', label: 'Awaiting approval', statuses: ['PendingApproval'] },
  { value: 'with-customer', label: 'With customer', statuses: ['Sent'] },
  { value: 'won', label: 'Won', statuses: ['Accepted', 'Converted'] },
  {
    value: 'awaiting-fulfillment',
    label: 'Awaiting fulfillment',
    statuses: [],
    fulfillmentStatus: 'AwaitingFulfillment',
  },
  { value: 'not-proceeding', label: 'Not proceeding', statuses: ['Declined', 'Expired', 'Revoked'] },
  { value: 'archived', label: 'Archived', statuses: [], archived: true },
] as const;

export function workflowStatuses(value: string): string | undefined {
  const tab = QUOTE_WORKFLOW_TABS.find((candidate) => candidate.value === value);
  return tab && tab.statuses.length > 0 ? tab.statuses.join(',') : undefined;
}

export function workflowFulfillmentStatus(value: string): FulfillmentStatusKey | undefined {
  const tab = QUOTE_WORKFLOW_TABS.find((candidate) => candidate.value === value);
  return tab && 'fulfillmentStatus' in tab ? tab.fulfillmentStatus : undefined;
}

export function workflowArchived(value: string): boolean | undefined {
  const tab = QUOTE_WORKFLOW_TABS.find((candidate) => candidate.value === value);
  return tab && 'archived' in tab ? tab.archived : undefined;
}

export const QUOTE_TYPE_TABS = [
  { value: 'all', label: 'All Types' },
  { value: 'NewBusiness', label: 'New Business' },
  { value: 'Renewal', label: 'Renewal' },
  { value: 'RenewalWithChanges', label: 'Renewal with Changes' },
  { value: 'Amendment', label: 'Amendment' },
  { value: 'ProofOfConcept', label: 'Proof of Concept' },
] as const;
